import { useAuth } from "@/context/AuthContext";
import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import {
  Compass, Sparkle, ArrowRight, BookOpenText, FlowArrow, Play,
  CheckCircle, Circuitry, UsersThree, ChartBar, CurrencyDollar, Megaphone, Warning, Handshake,
} from "@phosphor-icons/react";
import { Link } from "react-router-dom";

/** Compact formatter e.g. 1.2k */
const fmt = (n) => {
  if (n === null || n === undefined) return "—";
  if (n < 1000) return String(n);
  if (n < 10000) return (n / 1000).toFixed(1).replace(/\.0$/, "") + "k";
  return Math.round(n / 1000) + "k";
};

const fmtMoney = (n) => {
  if (n === null || n === undefined) return "—";
  if (n >= 1_000_000) return "$" + (n / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
  if (n >= 10_000) return "$" + Math.round(n / 1000) + "k";
  if (n >= 1000) return "$" + (n / 1000).toFixed(1).replace(/\.0$/, "") + "k";
  return "$" + Math.round(n);
};

function StatCard({ label, primary, secondary, meta, icon: Icon, disabled, testid }) {
  return (
    <div
      data-testid={testid}
      className="rounded-md border border-white/10 bg-graphite-900 p-6 flex flex-col gap-3"
    >
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.2em] text-ink-secondary">{label}</div>
        {Icon && (
          <div className="h-7 w-7 rounded-sm border border-white/10 bg-graphite-800 grid place-items-center">
            <Icon weight="duotone" className="h-3.5 w-3.5 text-ink-secondary" />
          </div>
        )}
      </div>
      {disabled ? (
        <>
          <div className="font-display text-4xl font-light text-ink-primary/40">—</div>
          <div className="text-[11px] text-ink-secondary">Not enabled for this workspace</div>
        </>
      ) : (
        <>
          <div className="font-display text-4xl font-light text-ink-primary leading-none">{primary}</div>
          {secondary && <div className="text-xs text-ink-secondary">{secondary}</div>}
          {meta && <div className="mt-1 text-[11px] text-ink-secondary/80">{meta}</div>}
        </>
      )}
    </div>
  );
}

function SectionCard({ testid, eyebrow, EyebrowIcon, title, children, className = "" }) {
  return (
    <div className={`rounded-md border border-white/10 bg-graphite-900 p-6 md:p-8 ${className}`} data-testid={testid}>
      {eyebrow && (
        <div className="text-[10px] uppercase tracking-[0.2em] text-ink-secondary flex items-center gap-1.5">
          {EyebrowIcon && <EyebrowIcon weight="duotone" className="h-3 w-3" />}
          {eyebrow}
        </div>
      )}
      {title && <h3 className="mt-1 font-display text-xl tracking-tight">{title}</h3>}
      {children}
    </div>
  );
}

// ---------- Individual widgets ----------
function BusinessOverviewWidget({ data, loading }) {
  return (
    <div className="col-span-12 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6" data-testid="widget-business-overview">
      <StatCard testid="widget-bo-leads" label="Total leads" icon={UsersThree}
        primary={loading ? "—" : fmt(data?.total_leads)}
        secondary={<span data-testid="widget-bo-leads-count">{data?.total_leads ?? 0} in workspace</span>} />
      <StatCard testid="widget-bo-customers" label="Total customers" icon={UsersThree}
        primary={loading ? "—" : fmt(data?.total_customers)}
        secondary={<span data-testid="widget-bo-customers-count">{data?.total_customers ?? 0} converted</span>} />
      <StatCard testid="widget-bo-revenue" label="Total revenue" icon={CurrencyDollar}
        primary={loading ? "—" : fmtMoney(data?.total_revenue)}
        secondary={<span data-testid="widget-bo-revenue-raw">${(data?.total_revenue ?? 0).toLocaleString()}</span>} />
      <StatCard testid="widget-bo-campaigns" label="Active campaigns" icon={Megaphone}
        primary={loading ? "—" : fmt(data?.active_campaigns)}
        secondary={<span data-testid="widget-bo-campaigns-count">{data?.active_campaigns ?? 0} running</span>} />
    </div>
  );
}

function LeadsByStageWidget({ data, loading }) {
  const total = (data?.stages || []).reduce((a, b) => a + (b.count || 0), 0);
  return (
    <SectionCard testid="widget-leads-by-stage" className="col-span-12 lg:col-span-6"
      eyebrow={data?.pipeline_name ? `Pipeline · ${data.pipeline_name}` : "Leads by stage"}
      EyebrowIcon={ChartBar} title="Where your pipeline lives">
      {loading ? (
        <div className="mt-5 text-sm text-ink-secondary">Loading…</div>
      ) : (data?.stages || []).length === 0 ? (
        <div className="mt-5 text-sm text-ink-secondary" data-testid="widget-lbs-empty">
          No default pipeline yet. Create one from the Leads page.
        </div>
      ) : (
        <>
          <ul className="mt-5 space-y-3">
            {data.stages.map((s) => {
              const pct = total ? Math.round((s.count / total) * 100) : 0;
              return (
                <li key={s.stage_id} className="flex items-center gap-4" data-testid={`widget-lbs-row-${s.stage_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-sm text-ink-primary truncate">{s.stage_name}</span>
                      <span className="text-xs text-ink-secondary shrink-0">
                        <span data-testid={`widget-lbs-count-${s.stage_id}`}>{s.count}</span>
                        <span className="opacity-60"> · {pct}%</span>
                      </span>
                    </div>
                    <div className="mt-1.5 h-1.5 rounded-full bg-graphite-800 border border-white/5 overflow-hidden">
                      <div className="h-full bg-white/70" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
          <div className="mt-5 text-[11px] text-ink-secondary">
            <span data-testid="widget-lbs-total">{total}</span> total leads in default pipeline
          </div>
        </>
      )}
    </SectionCard>
  );
}

function AIInsightsWidget({ data, onGenerate, generating }) {
  const items = data?.items || [];
  const insufficient = !!data?.insufficient_data;
  const ts = data?.generated_at ? new Date(data.generated_at) : null;
  return (
    <div className="col-span-12 rounded-md border border-white/10 bg-graphite-900 p-6 md:p-8" data-testid="widget-ai-insights">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-ink-secondary flex items-center gap-1.5">
            <Sparkle weight="duotone" className="h-3 w-3" /> Growth intelligence
          </div>
          <h3 className="mt-1 font-display text-xl tracking-tight">AI Insights</h3>
          {ts && (
            <div className="mt-1 text-[11px] text-ink-secondary" data-testid="widget-ai-insights-ts">
              Last analyzed: {ts.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}
            </div>
          )}
        </div>
        <button data-testid="widget-ai-insights-generate" onClick={onGenerate} disabled={generating}
          className="inline-flex items-center gap-2 rounded-full bg-white text-graphite-950 hover:bg-neutral-200 disabled:opacity-60 px-4 py-2 text-sm font-medium transition-colors">
          <Sparkle weight="fill" className="h-3.5 w-3.5" />
          {generating ? "Analyzing…" : ts ? "Regenerate" : "Generate insights"}
        </button>
      </div>
      {insufficient ? (
        <div className="mt-6 text-sm text-ink-secondary" data-testid="widget-ai-insights-empty">
          Not enough data yet — add a few leads, a customer, or an active campaign, then try again.
        </div>
      ) : items.length === 0 ? (
        <div className="mt-6 text-sm text-ink-secondary">
          Click <span className="text-ink-primary">Generate insights</span> to have Claude review your workspace and surface 3-5 grounded observations.
        </div>
      ) : (
        <ul className="mt-6 space-y-3" data-testid="widget-ai-insights-list">
          {items.map((it, i) => (
            <li key={i} data-testid={`widget-ai-insights-item-${i}`}
              className="flex items-start gap-3 rounded-md border border-white/10 bg-graphite-950/40 px-4 py-3 text-sm text-ink-primary leading-relaxed">
              <span className="mt-1 h-1.5 w-1.5 rounded-full bg-white/70 shrink-0" />
              {it}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function NurtureDueTodayWidget({ data, loading }) {
  const rows = data || [];
  return (
    <SectionCard testid="widget-nurture-due-today" className="col-span-12 lg:col-span-6"
      eyebrow="Nurture" EyebrowIcon={Warning} title="Due today &amp; overdue">
      {loading ? (
        <div className="mt-5 text-sm text-ink-secondary">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="mt-5 text-sm text-ink-secondary" data-testid="widget-ndt-empty">
          Nothing due right now. Steps are on track.
        </div>
      ) : (
        <div className="mt-5 divide-y divide-white/5 rounded-md border border-white/10 overflow-hidden">
          {rows.slice(0, 10).map((r) => (
            <div key={r.enrollment_id} data-testid={`widget-ndt-row-${r.enrollment_id}`}
              className="px-4 py-3 flex items-center justify-between gap-3 hover:bg-white/[0.02]">
              <div className="min-w-0">
                <Link to={`/leads`} className="text-sm text-ink-primary hover:text-white truncate block">
                  {r.lead_name || "(unnamed lead)"}
                </Link>
                <Link to={`/nurture-sequences/${r.sequence_id}`}
                  className="text-[11px] text-ink-secondary hover:text-ink-primary truncate block">
                  {r.sequence_name || "Sequence"} · Step {r.current_step_order}
                </Link>
              </div>
              <span data-testid={`widget-ndt-due-${r.enrollment_id}`}
                className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm border shrink-0 ${
                  r.due_status === "overdue"
                    ? "border-red-400/30 text-red-300"
                    : "border-amber-400/30 text-amber-200"
                }`}>
                {r.due_status === "overdue" ? "Overdue" : "Due today"}
              </span>
            </div>
          ))}
          {rows.length > 10 && (
            <div className="px-4 py-2 text-[11px] text-ink-secondary">
              +{rows.length - 10} more
            </div>
          )}
        </div>
      )}
    </SectionCard>
  );
}

function PartnerAttributionWidget({ data, loading }) {
  const rows = data || [];
  return (
    <SectionCard testid="widget-partner-attribution" className="col-span-12 lg:col-span-6"
      eyebrow="Partners" EyebrowIcon={Handshake} title="Top partners this month">
      {loading ? (
        <div className="mt-5 text-sm text-ink-secondary">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="mt-5 text-sm text-ink-secondary" data-testid="widget-partner-attribution-empty">
          No partner-attributed customers yet this month.
        </div>
      ) : (
        <ul className="mt-5 space-y-3" data-testid="widget-partner-attribution-list">
          {rows.map((r, idx) => {
            const top = rows[0]?.customer_count || 1;
            const pct = Math.round(((r.customer_count || 0) / top) * 100);
            return (
              <li key={r.partner_id}
                data-testid={`widget-partner-attribution-row-${r.partner_id}`}
                className="flex items-center gap-4">
                <div className="w-6 text-[11px] font-mono text-ink-secondary tabular-nums">
                  {idx + 1}.
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-3">
                    <Link to="/partners" className="text-sm text-ink-primary hover:text-white truncate">
                      {r.partner_name}
                      {!r.is_active && (
                        <span className="ml-2 text-[10px] uppercase tracking-widest text-ink-secondary/70">Inactive</span>
                      )}
                    </Link>
                    <span className="text-xs text-ink-secondary shrink-0" data-testid={`widget-partner-attribution-count-${r.partner_id}`}>
                      {r.customer_count} customer{r.customer_count === 1 ? "" : "s"}
                    </span>
                  </div>
                  <div className="mt-1.5 h-1.5 rounded-full bg-graphite-800 border border-white/5 overflow-hidden">
                    <div className="h-full bg-blue-300/70" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </SectionCard>
  );
}

function KBStatsWidget({ stats, loading }) {
  const kb = stats?.knowledge_base;
  return (
    <StatCard
      testid="dashboard-stat-knowledge-base"
      label="Knowledge base"
      icon={BookOpenText}
      disabled={!kb?.enabled}
      primary={loading ? "—" : fmt(kb?.recent)}
      secondary={
        <>
          <span data-testid="dashboard-kb-recent">{kb?.recent ?? 0} this month</span>{" · "}
          <span data-testid="dashboard-kb-total">{kb?.total ?? 0} total documents</span>
        </>
      }
    />
  );
}

function WorkflowStatsWidget({ stats, loading }) {
  const wf = stats?.workflows;
  const runs = stats?.workflow_runs;
  return (
    <div className="col-span-12 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6" data-testid="widget-workflow-stats">
      <StatCard testid="dashboard-stat-workflows" label="Workflows" icon={FlowArrow}
        disabled={!wf?.enabled}
        primary={loading ? "—" : fmt(wf?.total)}
        secondary={<span data-testid="dashboard-wf-total">{wf?.total ?? 0} registered</span>} />
      <StatCard testid="dashboard-stat-workflow-runs" label="Workflow runs" icon={Play}
        disabled={!runs?.enabled}
        primary={loading ? "—" : fmt(runs?.recent)}
        secondary={
          <>
            <span data-testid="dashboard-runs-recent">{runs?.recent ?? 0} this month</span>{" · "}
            <span data-testid="dashboard-runs-total">{runs?.total ?? 0} total</span>
          </>
        }
        meta={runs?.total ? (
          <span data-testid="dashboard-runs-success-alltime">
            {runs?.success_rate_all_time === null ? "—" : `${runs.success_rate_all_time}% success all-time`}
          </span>) : null} />
      <StatCard testid="dashboard-stat-workflow-success" label="Success (30d)" icon={CheckCircle}
        disabled={!runs?.enabled}
        primary={loading ? "—" : (runs?.success_rate_recent === null || runs?.success_rate_recent === undefined) ? "—" : `${runs.success_rate_recent}%`}
        secondary={<span>{runs?.recent ?? 0} runs</span>} />
    </div>
  );
}

function AutomationCategoriesWidget({ stats, loading }) {
  const wf = stats?.workflows;
  if (loading) {
    return <SectionCard testid="widget-automation-categories" className="col-span-12 lg:col-span-6"
      eyebrow="Workflow categories" EyebrowIcon={Circuitry} title="Where your automations live">
      <div className="mt-5 text-sm text-ink-secondary">Loading…</div>
    </SectionCard>;
  }
  if (!wf?.enabled || (wf?.total || 0) === 0 || !(wf?.by_category?.length)) {
    return <SectionCard testid="widget-automation-categories" className="col-span-12 lg:col-span-6"
      eyebrow="Workflow categories" EyebrowIcon={Circuitry} title="Where your automations live">
      <div className="mt-5 text-sm text-ink-secondary" data-testid="widget-ac-empty">
        No workflows registered yet.
      </div>
    </SectionCard>;
  }
  return (
    <div className="col-span-12 lg:col-span-6 rounded-md border border-white/10 bg-graphite-900 p-6 md:p-8"
      data-testid="dashboard-category-breakdown">
      <div className="text-[10px] uppercase tracking-[0.2em] text-ink-secondary flex items-center gap-1.5">
        <Circuitry weight="duotone" className="h-3 w-3" /> Workflow categories
      </div>
      <h3 className="mt-1 font-display text-xl tracking-tight">Where your automations live</h3>
      <ul className="mt-5 space-y-3">
        {(wf.by_category || []).map((row) => {
          const pct = wf.total ? Math.round((row.count / wf.total) * 100) : 0;
          return (
            <li key={row.category_id || "__uncategorized__"}
              data-testid={`dashboard-cat-row-${row.category_id || "uncategorized"}`}
              className="flex items-center gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-sm text-ink-primary truncate">
                    {row.name}
                    {row.active === false && <span className="ml-2 text-[10px] uppercase tracking-widest text-ink-secondary/70">Inactive</span>}
                  </span>
                  <span className="text-xs text-ink-secondary shrink-0">
                    <span data-testid={`dashboard-cat-count-${row.category_id || "uncategorized"}`}>{row.count}</span>
                    <span className="opacity-60"> · {pct}%</span>
                  </span>
                </div>
                <div className="mt-1.5 h-1.5 rounded-full bg-graphite-800 border border-white/5 overflow-hidden">
                  <div className="h-full bg-white/70" style={{ width: `${pct}%` }} />
                </div>
              </div>
            </li>);
        })}
      </ul>
      <div className="mt-5 text-[11px] text-ink-secondary">
        <span data-testid="dashboard-cat-total">{wf.total}</span> workflows in this workspace
      </div>
    </div>
  );
}

// ---------- Main dashboard ----------
export default function Dashboard() {
  const { user, currentOrg } = useAuth();
  const first = (user?.name || user?.email || "").split(" ")[0] || "there";

  const [widgets, setWidgets] = useState([]);
  const [stats, setStats] = useState(null);
  const [bo, setBo] = useState(null);
  const [lbs, setLbs] = useState(null);
  const [ndt, setNdt] = useState(null);
  const [insights, setInsights] = useState(null);
  const [generatingInsights, setGeneratingInsights] = useState(false);
  const [partnerAttrib, setPartnerAttrib] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const wRes = await api.get("/dashboard/widgets");
      const wlist = wRes.data || [];
      setWidgets(wlist);
      const enabledIds = new Set(wlist.filter((w) => w.enabled).map((w) => w.widget_id));
      // Only fetch what we'll show
      const jobs = [];
      if (enabledIds.has("business_overview")) jobs.push(api.get("/dashboard/business-overview").then(r => setBo(r.data)));
      if (enabledIds.has("leads_by_stage")) jobs.push(api.get("/dashboard/leads-by-stage").then(r => setLbs(r.data)).catch(() => setLbs(null)));
      if (enabledIds.has("nurture_due_today")) jobs.push(api.get("/dashboard/nurture-due-today").then(r => setNdt(r.data)).catch(() => setNdt([])));
      if (enabledIds.has("ai_insights")) jobs.push(api.get("/dashboard/ai-insights").then(r => setInsights(r.data)).catch(() => setInsights(null)));
      if (enabledIds.has("partner_attribution")) jobs.push(api.get("/dashboard/partner-attribution").then(r => setPartnerAttrib(r.data)).catch(() => setPartnerAttrib([])));
      if (enabledIds.has("kb_stats") || enabledIds.has("workflow_stats") || enabledIds.has("automation_categories")) {
        jobs.push(api.get("/dashboard/stats").then(r => setStats(r.data)));
      }
      await Promise.all(jobs);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll, currentOrg?.org_id]);

  const enabledWidgets = widgets.filter((w) => w.enabled);
  const hasAnyActivity = !!(stats?.knowledge_base?.total || stats?.workflows?.total || stats?.workflow_runs?.total);

  const renderWidget = (w) => {
    switch (w.widget_id) {
      case "business_overview": return <BusinessOverviewWidget key={w.widget_id} data={bo} loading={loading} />;
      case "leads_by_stage": return <LeadsByStageWidget key={w.widget_id} data={lbs} loading={loading} />;
      case "nurture_due_today": return <NurtureDueTodayWidget key={w.widget_id} data={ndt} loading={loading} />;
      case "partner_attribution": return <PartnerAttributionWidget key={w.widget_id} data={partnerAttrib} loading={loading} />;
      case "ai_insights": return <AIInsightsWidget key={w.widget_id} data={insights} generating={generatingInsights}
        onGenerate={async () => {
          setGeneratingInsights(true);
          try {
            const { data } = await api.post("/dashboard/ai-insights/generate");
            setInsights(data);
          } catch (e) {
            // best-effort toast — Dashboard imports no toast library, keep silent
            console.warn("AI Insights generate failed", e);
          } finally { setGeneratingInsights(false); }
        }} />;
      case "kb_stats": return (
        <div key={w.widget_id} className="col-span-12 md:col-span-4">
          <KBStatsWidget stats={stats} loading={loading} />
        </div>);
      case "workflow_stats": return <WorkflowStatsWidget key={w.widget_id} stats={stats} loading={loading} />;
      case "automation_categories": return <AutomationCategoriesWidget key={w.widget_id} stats={stats} loading={loading} />;
      default: return null;
    }
  };

  return (
    <div className="min-h-screen p-8 md:p-12" data-testid="dashboard-page">
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] text-ink-secondary">
            {currentOrg?.name || "Workspace"}
          </div>
          <h1 className="mt-2 font-display text-4xl md:text-5xl font-light tracking-tight" data-testid="dashboard-greeting">
            Welcome, <span className="text-ink-primary">{first}</span>.
          </h1>
          <p className="mt-3 text-ink-secondary text-sm max-w-lg">
            Your control room for leads, revenue, nurture, and workflows.
          </p>
        </div>
        <div className="hidden md:flex items-center gap-2 text-[11px] uppercase tracking-widest text-ink-secondary border border-white/10 rounded-full px-3 py-1.5">
          <Sparkle weight="duotone" className="h-3.5 w-3.5" />
          Live overview
        </div>
      </div>

      <div className="mt-10 grid grid-cols-12 gap-4 md:gap-6" data-testid="dashboard-widget-grid">
        {enabledWidgets.length === 0 && !loading ? (
          <div className="col-span-12 rounded-md border border-white/10 bg-graphite-900 p-8 min-h-[220px] flex flex-col items-center justify-center text-center"
            data-testid="dashboard-empty">
            <Compass weight="duotone" className="h-6 w-6 text-ink-secondary" />
            <h2 className="mt-3 font-display text-2xl tracking-tight">Your dashboard is empty</h2>
            <p className="mt-2 text-sm text-ink-secondary max-w-md">
              A platform admin can turn on widgets for this workspace from the Platform admin area.
            </p>
            <Link to="/profile" data-testid="dashboard-profile-cta"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-white text-graphite-950 px-5 py-2.5 text-sm font-medium hover:bg-neutral-200 transition-colors">
              Finish your profile <ArrowRight weight="bold" className="h-4 w-4" />
            </Link>
          </div>
        ) : (
          enabledWidgets.map(renderWidget)
        )}
      </div>

      {!loading && hasAnyActivity === false && enabledWidgets.length > 0 && (
        <p className="mt-6 text-[11px] text-ink-secondary">
          Data will populate here as your workspace fills in.
        </p>
      )}
    </div>
  );
}
